<template>
  <div>
    <b-modal
      id="editErrorModal"
      size="lg"
      content-class="menu-bg-blue-transparent-modal"
      title="Delivery Review Tool Update Error"
      hide-footer
     
    >
      <template #modal-header="{ close }">
        <div class="ae-header">
          <span class="ae-header-icon">
            <fa :icon="['fas', 'pencil-alt']" />
          </span>
          <div class="ae-header-text">
            <h5 class="ae-title">Update Error</h5>
            <p class="ae-subtitle">Review and update error details</p>
          </div>
          <button type="button" class="ae-close" title="Close" @click="close()">
            <fa :icon="['fas', 'times']" />
          </button>
        </div>
      </template>

      <!-- WEBGIS-2568 -- Added conditional statement to make sure data is rendered in window -->
      <b-container v-if="updateErrorDataSource">
        <div class="ae-info">
        <b-row align-h="start" class="pt-1" no-gutters>
          <b-col cols="6">
            <b-row no-gutters>
              <b-col cols="4">Error ID: </b-col>
              <b-col cols="8" class="data-font">{{ updateErrorDataSource.id }}</b-col>
            </b-row>
          </b-col>
          <b-col cols="6" v-if="updateErrorDataSource && updateErrorDataSource.region" no-gutters>
            <b-row no-gutters>
              <b-col cols="4">Region: </b-col>
              <b-col cols="8" class="data-font">{{updateErrorDataSource.region}}</b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row align-h="start" class="pt-1" no-gutters>
          <b-col cols="6">
            <b-row no-gutters>
              <b-col cols="4">Submitted By: </b-col>
              <b-col cols="8" class="data-font">{{ updateErrorDataSource.created_user }}</b-col>
            </b-row>
          </b-col>
          <b-col cols="6" v-if="updateErrorDataSource && (updateErrorDataSource.country || updateErrorDataSource.province)" no-gutters>
            <b-row no-gutters>
              <b-col cols="4">Country,Province: </b-col>
              <b-col cols="8" class="data-font">{{ locationTag }}</b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row align-h="start" class="pt-1" no-gutters>
          <b-col cols="6">
            <b-row no-gutters>
              <b-col cols="4">Submitted Date: </b-col>
              <b-col cols="8" class="data-font">{{ updateErrorDataSource.created_date | formatDate }}</b-col>
            </b-row>
          </b-col>
          <b-col cols="6" v-if="updateErrorDataSource && updateErrorDataSource.lat_lon" no-gutters>
            <b-row no-gutters>
              <b-col cols="4">LAT/LON: </b-col>
              <b-col cols="8" class="data-font">{{updateErrorDataSource.lat_lon}}</b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row align-h="start" class="pt-1" no-gutters>
          <b-col cols="6">
            <b-row no-gutters>
              <b-col cols="4">Updated By: </b-col>
              <b-col cols="8" class="data-font">{{ updateErrorDataSource.updated_user }}</b-col>
            </b-row>
          </b-col>
          <b-col cols="6" no-gutters>
            <b-row no-gutters>
              <b-col cols="4">Update Date: </b-col>
              <b-col cols="8" class="data-font">{{ updateErrorDataSource.updated_date | formatDate }}</b-col>
            </b-row>
          </b-col>
        </b-row>
        </div>
        <b-row class="pt-4">
          <b-col>
            <label> Layer: </label>
            <b-form-input
              v-model="updateErrorDataSource.layer_name"
              disabled
            />
          </b-col>
          <b-col>
            <label> Dataset: </label>
            <b-form-input
              v-model="updateErrorDataSource.dataset"
              disabled
            />
          </b-col>
          <b-col>
            <label> PDT Issue ID: </label>
            <b-form-input
              v-model="updateErrorDataSource.issue_id"
              :disabled="isExpired"
            />
          </b-col>
        </b-row>
        <b-row class="pt-2">
          <b-col>
            <label> Layer Attribute: </label>
            <b-form-input
              v-model="updateErrorDataSource.layer_attribute"
              disabled
            />
          </b-col>
          <b-col>
            <label> Error Type: </label>
            <b-form-input
              v-model="updateErrorDataSource.error_type"
              disabled
            />
          </b-col>
          <b-col>
            <label> Severity: </label>
            <b-form-select
              v-model="updateErrorDataSource.severity"
              :options="severityOptions"
              :disabled="isExpired"
            ></b-form-select>
          </b-col>
        </b-row>
        <b-row class="pt-2">
          <b-col>
            <label> Comments: </label>
            <b-form-textarea
              rows="2"
              v-model="updateErrorDataSource.comments"
              disabled
            ></b-form-textarea>
          </b-col>
        </b-row>
        <b-row class="pt-2">
          <b-col>
            <label> Status: </label>
            <b-form-select
              v-model="updateErrorDataSource.status"
              :options="statusOptions"
              :disabled="isExpired"
            ></b-form-select>
          </b-col>
        </b-row>

        <b-row class="pt-2">
          <b-col>
            <label> Notes: </label>
            <b-form-textarea
              rows="5"
              v-model="updateErrorDataSource.notes"
              :disabled="isExpired"
            ></b-form-textarea>
          </b-col>
        </b-row>

        <b-row class="pt-3">
          <b-col>
            <label>Snapshots</label>
            <div v-if="loadingSnapshots" class="text-center py-2">
              <b-spinner small></b-spinner>
            </div>
            <div v-else-if="snapshots.length" class="snapshot-gallery">
              <img
                v-for="snap in snapshots"
                :key="snap.id"
                :src="snapshotUrl(snap.path)"
                :title="snap.created_user + ' · ' + formatDate(snap.created_date)"
                class="snapshot-thumb"
                @click="openSnapshot(snap.path)"
              />
            </div>
            <div v-else class="text-muted small py-1">No snapshots saved for this error.</div>
          </b-col>
        </b-row>

        <b-row class="pt-2">
          <b-col>
            <button class="drt-update-btn" @click="updateError()" :disabled="isExpired">
              <span>Update Error</span>
            </button>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
  </div>
</template>

<style scoped>
.custom-select:disabled,
.form-control:disabled {
  background-color: #dfdfdf !important;
  opacity: 1 !important;
  color: #495057 !important;
}
.region-label-Update {
  /* position: absolute;
  margin-top: -3rem;
  margin-left: 64rem; */
  position: relative;
  margin-top: -3rem;    
  text-align: right;
  margin-right: 25px;
  margin-bottom: 30px;
}

.data-font {
  font-weight: 100 !important;
  font-size: 15px !important;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 2px;
}
.snapshot-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.snapshot-thumb {
  height: 80px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.2);
  cursor: pointer;
  transition: opacity 0.15s;
  object-fit: cover;
}
.snapshot-thumb:hover {
  opacity: 0.8;
}
.text-muted{
  color: #a7ad6c !important;
}
</style>

<style>
/* ── Beautified Update Error modal ── (id-scoped, self-contained) */
#editErrorModal,
#editErrorModal * {
  font-family: 'Inter Regular', 'Open Sans', sans-serif !important;
}

/* Right-docked, same location as the Add Error modal */
#editErrorModal.modal {
  padding-right: 0 !important;
}
#editErrorModal .modal-dialog {
  position: fixed;
  right: 2%;
  top: 2%;
  margin: 0;
  height: auto;
  max-height: 96vh;
  width: 600px;
  max-width: 600px;
  transform: none !important;
}
#editErrorModal .modal-content {
  height: auto;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  border-radius: 12px !important;
  box-shadow: 0 20px 50px rgba(6, 60, 109, 0.35) !important;
  overflow: hidden;
}
#editErrorModal .modal-body {
  flex: 1;
  overflow-y: auto;
}
#editErrorModal .modal-header {
  border-radius: 12px 12px 0 0 !important;
  padding: 0.9rem 1.3rem;
}
#editErrorModal .modal-body {
  padding: 1rem 1.6rem 1rem;
}

/* Custom header */
#editErrorModal .ae-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}
#editErrorModal .ae-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 1rem;
  flex-shrink: 0;
}
#editErrorModal .ae-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.01em;
}
#editErrorModal .ae-subtitle {
  margin: 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
}
#editErrorModal .ae-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, transform 0.15s ease;
}
#editErrorModal .ae-close:hover {
  background: rgba(255, 255, 255, 0.28);
  transform: rotate(90deg);
}

/* Read-only info panel */
#editErrorModal .ae-info {
  background: linear-gradient(180deg, #f6fafd 0%, #eef4fa 100%);
  border: 1px solid rgba(6, 60, 109, 0.08);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 1.25rem;
}
/* Stack each label above its value so long labels never collide with values */
#editErrorModal .ae-info .col-4,
#editErrorModal .ae-info .col-8 {
  flex: 0 0 100%;
  max-width: 100%;
  display: block;
}
#editErrorModal .ae-info .col-4 {
  color: #7d9bb3;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.3;
  white-space: nowrap;
}
#editErrorModal .ae-info .data-font {
  display: block !important;
  color: #063c6d !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  margin-top: 0 !important;
  line-height: 1.35;
}
#editErrorModal .ae-info > .row {
  margin-bottom: 8px;
}
#editErrorModal .ae-info > .row:last-child {
  margin-bottom: 0;
}

/* Sleek labels */
#editErrorModal label {
  display: block;
  font-size: 0.76rem;
  font-weight: 600;
  color: #063c6d;
  margin-bottom: 6px;
}

/* Consistent modern inputs */
#editErrorModal .form-control,
#editErrorModal .custom-select {
  border: 1px solid rgba(6, 60, 109, 0.2) !important;
  border-radius: 8px !important;
  color: #063c6d;
  font-size: 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
#editErrorModal input.form-control,
#editErrorModal .custom-select {
  height: 34px;
}
#editErrorModal .form-control:focus,
#editErrorModal .custom-select:focus {
  border-color: #4a9fe0 !important;
  box-shadow: 0 0 0 3px rgba(74, 159, 224, 0.22) !important;
}
#editErrorModal textarea.form-control {
  min-height: 60px;
}

/* Roomier row spacing */
#editErrorModal .modal-body .row.pt-2 {
  padding-top: 0 !important;
  margin-top: 1rem !important;
}
#editErrorModal .modal-body .row.pt-3 {
  padding-top: 0 !important;
  margin-top: 1.25rem !important;
}
#editErrorModal .modal-body .row.pt-4 {
  padding-top: 0 !important;
  margin-top: 1.6rem !important;
}

#editErrorModal .text-muted {
  color: #8ab0cc !important;
}

/* Modern update button */
#editErrorModal .drt-update-btn {
 position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 8px 0;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 55%, #0f8a3d 100%);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  box-shadow: 0 6px 18px rgba(22, 163, 74, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}
#editErrorModal .drt-update-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -80%;
  width: 60%;
  height: 100%;
  background: linear-gradient(105deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: skewX(-18deg);
  transition: left 0.55s ease;
  pointer-events: none;
}
#editErrorModal .drt-update-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.08);
  box-shadow: 0 10px 26px rgba(6, 60, 109, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}
#editErrorModal .drt-update-btn:hover:not(:disabled)::before {
  left: 130%;
}
#editErrorModal .drt-update-btn:active:not(:disabled) {
  transform: translateY(1px);
}
#editErrorModal .drt-update-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
#editErrorModal .drt-update-icon {
  font-size: 0.8rem;
}
</style>

<script>
import axios from 'axios'
export default {
  name: 'MMP_DRT_UpdateError',
  props: ['severityOptions', 'statusOptions', 'updateErrorDataSource', 'userEmail', 'user'],
  watch: {
    updateErrorDataSource: function (newValue) {
      if (!newValue) return
      if (!newValue.archived_date) {
        this.isExpired = false
      } else {
        const diffDays = (new Date() - new Date(newValue.archived_date)) / (1000 * 60 * 60 * 24)
        this.isExpired = diffDays >= 90
      }
      this.fetchSnapshots(newValue.id)
    }
  },
  data() {
    return {
      isExpired: false,
      snapshots: [],
      loadingSnapshots: false
    }
  },
  methods: {
    fetchSnapshots(errorId) {
      if (!errorId) return
      this.snapshots = []
      this.loadingSnapshots = true
      axios.get(process.env.API_LOCATION + '/api/error/getErrorSnapshots/' + errorId, {
        headers: { 'x-auth-token': sessionStorage.token }
      })
      .then(res => { this.snapshots = res.data })
      .catch(err => console.log('fetchSnapshots error:', err))
      .finally(() => { this.loadingSnapshots = false })
    },
    snapshotUrl(filePath) {
      return process.env.API_LOCATION + '/' + filePath
    },
    openSnapshot(filePath) {
      window.open(this.snapshotUrl(filePath), '_blank')
    },
    formatDate(originalDate) {
      if (!originalDate) return ''
      let dateArr = originalDate.split('-')
      let day = dateArr[2].substring(0, dateArr[2].length - 1)
      return dateArr[1] + '-' + day + '-' + dateArr[0]
    },
    updateError() {
      /* Both processed variables are checking to make sure they are
      not null. If they aren't null, they need to have 1 subtracted
      from them because the errors database table is 0-indexed while
      the error_severity and error_status database tables are both
      1-indexed (id columns). If these are null, then just return
      them. */
      let processedSeverity =
        this.updateErrorDataSource.severity != null
          ? parseInt(this.updateErrorDataSource.severity) - 1
          : this.updateErrorDataSource.severity
      let processedStatus =
        this.updateErrorDataSource.status != null
          ? parseInt(this.updateErrorDataSource.status) - 1
          : this.updateErrorDataSource.status

      const selectedSeverityText =
          this.severityOptions.find(option => option.value === this.updateErrorDataSource.severity)?.text;

      const selectedStatusText =
        this.statusOptions.find(option => option.value === this.updateErrorDataSource.status)?.text;
        
      if (sessionStorage.token) {
        let paramsObj = {
          notes: this.updateErrorDataSource.notes,
          severity: processedSeverity,
          status: processedStatus,
          updated_user: this.userEmail,
          issue_id: this.updateErrorDataSource.issue_id,
          error_id: this.updateErrorDataSource.id,
          selectedSeverityText: selectedSeverityText,
          selectedStatusText: selectedStatusText
        }
        axios
        .get(
          process.env.API_LOCATION +
            '/api/error/updateError/' +
            this.updateErrorDataSource.id,
          {
            method: 'put',
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': sessionStorage.token
            },
            params: paramsObj
          }
        )
        .then(response => {
          const content = response.data
          if (!content.errors) {
            this.$emit('refresh', 'errors')
            this.$bvModal.hide('editErrorModal')
            this.$bvToast.toast('Error updated successfully', {
              autoHideDelay: 10000,
              variant: 'success',
              solid: true,
              title: 'Updated successfully',
              toaster: 'b-toaster-bottom-center'
            })
            this.sendNotification(paramsObj);
          } else {
            console.log('updateError API Call Error: ' + content.errors)
          }
        })
        .catch(error => {
          console.log(error)
        })
      } else {
        console.log('No User Token')
      }
    },
    sendNotification(paramsObj) {
      const d = new Date();

      const timestamp = d.toLocaleDateString(navigator.language, { month:'2-digit', day: '2-digit', year: 'numeric'}) 
                      + ", " + d.toLocaleTimeString(navigator.language, {hour: '2-digit', minute:'2-digit'})

      let msg = `An error has been updated by ${this.user.name}.
                  
                  Error Updated: 
                  **Error ID: ${paramsObj.error_id}
                  **PDT Issue ID: ${paramsObj.issue_id}

                  New Error Details:
                  **Severity: ${paramsObj.selectedSeverityText}
                  **Status: ${paramsObj.selectedStatusText}
                  **Updated Date: ${timestamp}
                  
                  **Notes: ${paramsObj.notes}`

      this.$emit('notification', msg)
    },
  },
  filters: {
    formatDate(originalDate) {
      /* This filter formats a date from
      yyyy-mm-ddZ format to mm-dd-yyyy format.
      Ex. 2021-05-20Z => 05-20-2021 */
      if (!originalDate) return ''
      let dateArr = originalDate.split('-')
      let year = dateArr[0]
      let month = dateArr[1]
      let day = dateArr[2]
      // Removes the "Z" from the day
      day = day.substring(0, day.length-1)
      return month + '-' + day + '-' + year
    },
  },
  computed: {
    locationTag() {
      let country = this.updateErrorDataSource.country
      let province = this.updateErrorDataSource.province
      if (country && province) {
        return `${country},${province}`
      } else if (country && !province) {
        return `${country}`
      } else if (!country && province) {
        return `${province}`
      } else {
        return ''
      }
    }
  }
}
</script>
